import React, { useEffect, useState } from 'react';
import { ArrowUpFromLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { formatWalletAddress, getDisplayName } from '../../services/authStorage';
import { usdtService } from '../../services/usdtService';
import { formatMoney } from '../../utils/formatMoney';

export function UsdtWithdrawForm() {
  const { isAuthenticated, user } = useAuth();
  const { balance, refreshWallet } = useWallet();
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    usdtService.getConfig().then(setConfig);
  }, [isAuthenticated]);

  const handleWithdraw = () => {
    const numericAmount = Number(amount);
    const minWithdraw = config?.minWithdraw ?? 1;

    if (!numericAmount || numericAmount < minWithdraw) {
      setMessage(`ถอนขั้นต่ำ ${formatMoney(minWithdraw)}`);
      return;
    }

    if (numericAmount > balance) {
      setMessage('ยอดในกระเป๋าเว็บไซต์ไม่เพียงพอ');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    usdtService
      .withdraw(numericAmount)
      .then((result) => {
        setMessage(result.message || 'ถอนเงินสำเร็จ');
        setAmount('');
        return refreshWallet();
      })
      .catch((error) => {
        setMessage(usdtService.getErrorMessage(error) || 'ถอนเงินไม่สำเร็จ');
      })
      .finally(() => setIsSubmitting(false));
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-600">
          กรุณา Connect Wallet ก่อนถอนเงิน
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-brand-red/20">
      <CardHeader className="bg-red-50/50">
        <CardTitle className="flex items-center gap-2 text-brand-red">
          <ArrowUpFromLine className="h-5 w-5" />
          ถอนเงิน
        </CardTitle>
        <p className="mt-1 text-sm text-gray-600">
          ถอนเข้ากระเป๋า Digital ที่เชื่อมไว้
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {config?.mockMode ? (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            โหมดทดสอบ — ไม่โอน on-chain จริง
          </div>
        ) : null}

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
          <p className="text-gray-600">ถอนไปยัง wallet</p>
          <p className="mt-1 font-mono font-semibold text-brand-red">
            {getDisplayName(user)}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {formatWalletAddress(user?.address)}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            ยอดในกระเป๋าเว็บไซต์: {formatMoney(balance)}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            จำนวนที่ต้องการถอน
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(event) =>
              setAmount(event.target.value.replace(/[^\d.]/g, ''))
            }
            placeholder="ระบุจำนวน เช่น 50"
            className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 focus:border-brand-red focus:outline-none"
          />
        </div>

        {message ? (
          <div
            className={`rounded-lg p-3 text-center text-sm ${
              message.includes('สำเร็จ') || message.includes('คำขอ')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-brand-red'
            }`}
          >
            {message}
          </div>
        ) : null}

        <Button
          size="lg"
          variant="gold"
          className="w-full"
          disabled={isSubmitting}
          onClick={handleWithdraw}
        >
          {isSubmitting ? 'กำลังถอน on-chain...' : 'ยืนยันถอนเงิน'}
        </Button>
      </CardContent>
    </Card>
  );
}
